import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import AppContext from "../../../AppContext";
import { AuthEmpleadoContext } from "../../../context/AuthEmpleadoContext";
import PanelLayout from "../../../layouts/PanelLayout";

const MESES = {
  "01": "Enero",
  "02": "Febrero",
  "03": "Marzo",
  "04": "Abril",
  "05": "Mayo",
  "06": "Junio",
  "07": "Julio",
  "08": "Agosto",
  "09": "Septiembre",
  10: "Octubre",
  11: "Noviembre",
  12: "Diciembre",
};

const formatearPeriodo = (periodo) => {
  if (!periodo) return "Período no informado";

  const periodoTexto = String(periodo).trim();
  const periodoAnioMes = periodoTexto.match(/^(\d{4})-(\d{2})$/);

  if (periodoAnioMes) {
    const [, anio, mes] = periodoAnioMes;
    return `${mes}/${anio}`;
  }

  return periodoTexto;
};

const obtenerMesDesdePeriodo = (periodo) => {
  const periodoFormateado = formatearPeriodo(periodo);
  const mes = periodoFormateado.match(/^(\d{2})\/\d{4}$/)?.[1];

  return MESES[mes] || "";
};

const obtenerMesSiguienteDesdePeriodo = (periodo) => {
  const periodoFormateado = formatearPeriodo(periodo);
  const mes = periodoFormateado.match(/^(\d{2})\/\d{4}$/)?.[1];

  if (!mes) return "";

  const mesSiguiente = (Number(mes) % 12);
  return MESES[String(mesSiguiente).padStart(2, "0")] || "";
};

const buscarPeriodoMensualEnTexto = (texto) => {
  const periodoEncontrado =
    texto.match(
      /Per(?:i|\u00ed)odo\s*:?\s*Mensual\s*(\d{2}\/\d{4})/i,
    ) ||
    texto.match(/Mensual\s*(\d{2}\/\d{4})/i);

  return periodoEncontrado?.[1] || "";
};

const bytesATexto = (bytes) => {
  let textoPdf = "";
  const chunkSize = 8192;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    textoPdf += String.fromCharCode(...chunk);
  }

  return textoPdf;
};

const extraerPeriodoDesdePdf = async (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  const textoPdf = bytesATexto(bytes);
  const periodoSinDescomprimir = buscarPeriodoMensualEnTexto(textoPdf);

  if (periodoSinDescomprimir) return periodoSinDescomprimir;

  if (typeof DecompressionStream === "undefined") return "";

  const streams = textoPdf.matchAll(/stream\r?\n([\s\S]*?)\r?\nendstream/g);

  for (const stream of streams) {
    try {
      const streamBytes = Uint8Array.from(stream[1], (char) =>
        char.charCodeAt(0),
      );
      const inflated = await new Response(
        new Blob([streamBytes]).stream().pipeThrough(
          new DecompressionStream("deflate"),
        ),
      ).arrayBuffer();
      const periodoDescomprimido = buscarPeriodoMensualEnTexto(
        bytesATexto(new Uint8Array(inflated)),
      );

      if (periodoDescomprimido) return periodoDescomprimido;
    } catch {
      // Algunos streams del PDF pueden no estar comprimidos con deflate.
    }
  }

  return "";
};

function MisRecibos() {
  const { empleado } = useContext(AuthEmpleadoContext);
  const [recibos, setRecibos] = useState([]);
  const [periodosPdf, setPeriodosPdf] = useState({});
  const [pdfsProcesados, setPdfsProcesados] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!empleado) return;

    let cancelado = false;

    const cargarRecibos = async () => {
      setLoading(true);
      setError(null);
      setPeriodosPdf({});
      setPdfsProcesados({});

      try {
        const res = await axios.get(
          `${AppContext.baseApiUrl}${AppContext.urlServiceRecibosEmpleado}${empleado.cuit}`,
        );

        if (cancelado) return;

        const recibosEmpleado = Array.isArray(res.data) ? res.data : [];
        setRecibos(recibosEmpleado);
        setLoading(false);

        const periodosExtraidos = {};
        const recibosProcesados = {};

        await Promise.all(
          recibosEmpleado.map(async (recibo) => {
            try {
              const pdf = await axios.get(
                `${AppContext.baseApiUrl}${AppContext.urlServiceReciboDescargar}${recibo.idReciboSueldo}`,
                { responseType: "arraybuffer" },
              );
              const periodoPdf = await extraerPeriodoDesdePdf(pdf.data);

              if (periodoPdf) {
                periodosExtraidos[recibo.idReciboSueldo] = periodoPdf;
              }
            } catch (error) {
              console.error("Error leyendo periodo del PDF:", error);
            } finally {
              recibosProcesados[recibo.idReciboSueldo] = true;
            }
          }),
        );

        if (!cancelado) {
          setPeriodosPdf(periodosExtraidos);
          setPdfsProcesados(recibosProcesados);
        }
      } catch (error) {
        if (!cancelado) {
          console.error("Error cargando recibos:", error);
          setError("No se pudieron cargar los recibos.");
          setRecibos([]);
          setLoading(false);
        }
      }
    };

    cargarRecibos();

    return () => {
      cancelado = true;
    };
  }, [empleado]);

  const descargar = async (recibo) => {
    try {
      const response = await axios.get(
        `${AppContext.baseApiUrl}${AppContext.urlServiceReciboDescargar}${recibo.idReciboSueldo}`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const periodoFormateado = formatearPeriodo(recibo.periodo);
      const periodoArchivo = (
        periodosPdf[recibo.idReciboSueldo] ||
        (periodoFormateado !== "Período no informado" ? periodoFormateado : "")
      ).replace("/", "-");
      const identificadorArchivo =
        recibo.idReciboSueldo || recibo.numeroRecibo || recibo.filename;
      const nombreArchivo = `recibo-${periodoArchivo || identificadorArchivo}.pdf`;

      const link = document.createElement("a");
      link.href = url;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error descargando recibo:", error);
      alert("No se pudo descargar el recibo.");
    }
  };

  return (
    <PanelLayout>
      <div className="container my-5">
        <div className="card shadow border-0">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">Mis Recibos de Sueldo</h5>
          </div>

          <div className="card-body">
            {loading && (
              <div className="text-center text-muted">Cargando recibos...</div>
            )}

            {!loading && error && (
              <div className="alert alert-danger text-center">{error}</div>
            )}

            {!loading && !error && recibos.length === 0 && (
              <div className="alert alert-info text-center">
                No hay recibos disponibles
              </div>
            )}

            {!loading && !error && recibos.length > 0 && (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Periodo</th>
                      <th>Recibo mes</th>
                      <th className="text-end">Descargar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recibos.map((r) => {
                      const periodoMensualPdf = periodosPdf[r.idReciboSueldo];
                      const periodoRecibo =
                        periodoMensualPdf || formatearPeriodo(r.periodo);
                      const mesRecibo =
                        obtenerMesDesdePeriodo(periodoMensualPdf) ||
                        obtenerMesSiguienteDesdePeriodo(r.periodo);

                      return (
                        <tr
                          key={
                            r.idReciboSueldo || r.numeroRecibo || r.filename
                          }
                        >
                          <td>{periodoRecibo}</td>
                          <td className="text-muted small">
                            {mesRecibo
                              ? `Recibo mes: ${mesRecibo}`
                              : pdfsProcesados[r.idReciboSueldo]
                                ? "No se pudo calcular el mes"
                                : "Leyendo PDF..."}
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-outline-success btn-sm"
                              onClick={() => descargar(r)}
                            >
                              Descargar PDF
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </PanelLayout>
  );
}

export default MisRecibos;
